import { useState, useEffect } from 'react';
import { db } from '@siaedts/shared/lib/supabase';

interface GalleryItem {
  id: string;
  title: string;
  description: string | null;
  category: string | null;
  image_url: string;
  thumbnail_url?: string | null;
  tags: string[];
}

export function Gallery() {
  const [categories, setCategories] = useState<string[]>(['All']);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [galleryItems, setGalleryItems] = useState<GalleryItem[]>([]);
  const [filteredItems, setFilteredItems] = useState<GalleryItem[]>([]);
  const [selectedItem, setSelectedItem] = useState<GalleryItem | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [viewMode, setViewMode] = useState<'grid' | 'masonry'>('grid');

  useEffect(() => {
    async function loadGallery() {
      try {
        const remoteItems = (await db.gallery.getAll({ is_published: true })) as Array<{
          id: string;
          title: string;
          description?: string | null;
          category?: string | null;
          image_url: string;
          thumbnail_url?: string | null;
        }>;

        const mappedItems: GalleryItem[] = (remoteItems ?? []).map((item) => ({
          id: item.id,
          title: item.title,
          description: item.description ?? 'Gallery item',
          category: item.category ?? 'General',
          image_url: item.image_url,
          thumbnail_url: item.thumbnail_url ?? item.image_url,
          tags: [item.category ?? 'Gallery'],
        }));

        const uniqueCategories = ['All', ...Array.from(new Set(mappedItems.map((item) => item.category ?? 'General').filter(Boolean)))];
        setCategories(uniqueCategories);
        setGalleryItems(mappedItems);
        setFilteredItems(mappedItems);
      } catch (error) {
        console.error('Failed to load gallery items:', error);
        setGalleryItems([]);
        setFilteredItems([]);
        setCategories(['All']);
      }
    }

    void loadGallery();
  }, []);

  useEffect(() => {
    let filtered = galleryItems;

    if (selectedCategory !== 'All') {
      filtered = filtered.filter((item) => item.category === selectedCategory);
    }

    setFilteredItems(filtered);
  }, [galleryItems, selectedCategory]);

  const openLightbox = (item: GalleryItem) => {
    setSelectedItem(item);
    setSelectedIndex(filteredItems.findIndex((galleryItem) => galleryItem.id === item.id));
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setSelectedItem(null);
    setSelectedIndex(-1);
    document.body.style.overflow = 'unset';
  };

  const showPreviousImage = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (filteredItems.length === 0) return;
    const nextIndex = selectedIndex <= 0 ? filteredItems.length - 1 : selectedIndex - 1;
    setSelectedIndex(nextIndex);
    setSelectedItem(filteredItems[nextIndex]);
  };

  const showNextImage = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (filteredItems.length === 0) return;
    const nextIndex = selectedIndex >= filteredItems.length - 1 ? 0 : selectedIndex + 1;
    setSelectedIndex(nextIndex);
    setSelectedItem(filteredItems[nextIndex]);
  };

  return (
    <>
      {/* Page Header */}
      <section className="bg-green-700 text-white py-16 sm:py-24">
        <div className="container-main">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl font-bold mb-4">Gallery</h1>
            <p className="text-green-100 text-lg">
              Explore our collection of equipment in action, facility tours, 
              event highlights, and customer success stories.
            </p>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-white border-b border-gray-200 sticky top-16 z-40">
        <div className="container-main">
          <div className="flex flex-wrap gap-2 justify-center" role="group" aria-label="Gallery categories">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                  selectedCategory === category
                    ? 'bg-green-700 text-white shadow-md'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                aria-pressed={selectedCategory === category}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="section bg-gray-50">
        <div className="container-main">
          <div className="mb-6 flex items-center justify-between">
            <p className="text-gray-600">
              Showing <span className="font-semibold text-gray-900">{filteredItems.length}</span> of <span className="font-semibold text-gray-900">{galleryItems.length}</span> images
            </p>
            <div className="flex items-center space-x-2" role="group" aria-label="View mode">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-lg transition-colors ${
                  viewMode === 'grid' ? 'bg-green-100 text-green-700' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                }`}
                aria-pressed={viewMode === 'grid'}
                aria-label="Grid view"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                </svg>
              </button>
              <button
                onClick={() => setViewMode('masonry')}
                className={`p-2 rounded-lg transition-colors ${
                  viewMode === 'masonry' ? 'bg-green-100 text-green-700' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                }`}
                aria-pressed={viewMode === 'masonry'}
                aria-label="Masonry view"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a2 2 0 012-2h6a2 2 0 012 2v4a2 2 0 01-2 2H9a2 2 0 01-2-2v-4zm5-9a2 2 0 00-2 2v8a2 2 0 002 2h2a2 2 0 002-2v-8a2 2 0 00-2-2h-2zm-6 4a2 2 0 012-2h2a2 2 0 012 2v4a2 2 0 01-2 2H7a2 2 0 01-2-2v-4z" />
                </svg>
              </button>
            </div>
          </div>

          {viewMode === 'grid' ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" role="list">
              {filteredItems.map((item) => (
                <article key={item.id} className="group relative aspect-square overflow-hidden rounded-xl bg-gray-100 cursor-pointer" role="listitem" onClick={() => openLightbox(item)}>
                  <img src={item.thumbnail_url ?? item.image_url} alt={item.title} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="absolute bottom-0 left-0 right-0 p-4 transform translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                    <div className="bg-white/95 backdrop-blur-sm rounded-lg p-3">
                      <span className="badge badge-blue text-xs mb-2 block">{item.category}</span>
                      <h3 className="font-semibold text-gray-900 text-sm mb-1 line-clamp-1">{item.title}</h3>
                      <p className="text-gray-600 text-xs line-clamp-2">{item.description}</p>
                    </div>
                  </div>
                  <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button 
                      className="btn-secondary p-2 rounded-full"
                      onClick={(e) => { e.stopPropagation(); openLightbox(item); }}
                      aria-label={`View ${item.title} in full size`}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </button>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-4" role="list" style={{ columnWidth: '300px' }}>
              {filteredItems.map((item) => (
                <article key={item.id} className="break-inside-avoid mb-4 relative overflow-hidden rounded-xl bg-gray-100 cursor-pointer group" role="listitem" onClick={() => openLightbox(item)}>
                  <div className="aspect-[4/3] bg-gray-100 flex items-center justify-center text-gray-400">
                    <img src={item.thumbnail_url ?? item.image_url} alt={item.title} className="h-full w-full object-cover" />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="absolute bottom-0 left-0 right-0 p-4 transform translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                    <div className="bg-white/95 backdrop-blur-sm rounded-lg p-3">
                      <span className="badge badge-blue text-xs mb-2 block">{item.category}</span>
                      <h3 className="font-semibold text-gray-900 text-sm mb-1 line-clamp-1">{item.title}</h3>
                      <p className="text-gray-600 text-xs line-clamp-2">{item.description}</p>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}

          {filteredItems.length === 0 && (
            <div className="text-center py-16 col-span-full">
              <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No images found</h3>
              <p className="text-gray-600 mb-6">Try selecting a different category</p>
              <button
                onClick={() => setSelectedCategory('All')}
                className="btn-outline"
              >
                Show All
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Lightbox Modal */}
      {selectedItem && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 p-4"
          onClick={closeLightbox}
          role="dialog"
          aria-modal="true"
          aria-labelledby="lightbox-title"
        >
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors p-2 rounded-full bg-black/50"
            aria-label="Close lightbox"
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          
          <div className="max-w-4xl w-full max-h-[90vh] flex flex-col">
            <div className="relative aspect-video max-h-[70vh] flex-shrink-0">
              <img src={selectedItem.image_url} alt={selectedItem.title} className="h-full w-full object-contain" />
            </div>
            
            <div className="bg-gray-900 p-6 rounded-b-xl">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <span className="badge badge-green mb-2">{selectedItem.category}</span>
                  <h2 id="lightbox-title" className="text-2xl font-bold text-white mb-2">{selectedItem.title}</h2>
                  <p className="text-gray-300">{selectedItem.description}</p>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-2">
                {selectedItem.tags.map((tag) => (
                  <span key={tag} className="badge badge-gray text-sm">{tag}</span>
                ))}
              </div>
            </div>
          </div>
          
          {/* Navigation */}
          <button
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white transition-colors p-3 rounded-full bg-black/50"
            onClick={showPreviousImage}
            aria-label="Previous image"
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white transition-colors p-3 rounded-full bg-black/50"
            onClick={showNextImage}
            aria-label="Next image"
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      )}
    </>
  );
}